import React, { useCallback, useEffect, useState } from 'react';
import VerificationInput from 'react-verification-input';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { usePosthogCapture } from 'auth/posthogCapture/_index';

import ErrorMessage from 'auth/components/ErrorMessage';
import AuthCard from 'auth/components/AuthCard';

import * as Styled from 'auth/styles/Card.style';
import { useLoginMutation, useLoginTokenMutation } from 'auth/hooks/loginApi';
import useCountdown from 'auth/hooks/useCountdown';

interface LocationState {
  email: string;
}

export default function EnterAccessCode(): JSX.Element {
  const auth = usePosthogCapture();
  const { t } = useTranslation('pageLogin');
  const { mutate: loginToken } = useLoginTokenMutation();
  const { mutate: login } = useLoginMutation();
  const location = useLocation<LocationState>();
  const { state } = location;
  const searchParams = new URLSearchParams(location.search);
  const emailParam = searchParams.get('email');
  const decodedEmail = emailParam
    ? decodeURIComponent(emailParam).replace(/ /g, '+')
    : null;
  const loginCodeParam = searchParams.get('login_code');

  const [error, setError] = useState('');
  const [resetKey, setResetKey] = useState(0);

  const email = decodedEmail || state.email;

  const isLockedOut = error === 'too many attempts';

  const { timeLeft, reset: resetCountdown } = useCountdown(60, {
    isActive: isLockedOut,
  });

  const maskedEmail = (() => {
    const [local, domain] = email.split('@');
    if (!domain) return email;
    const visible = local.slice(0, 3);
    return `${visible}${'*'.repeat(Math.max(local.length - 3, 3))}@${domain}`;
  })();

  const submitCode = useCallback(
    (inputCode: string) => {
      if (isLockedOut) return;

      setError('');

      loginToken(
        { email, login_code: inputCode },
        {
          onError: (e) => {
            if (e.status === 429) {
              setError('too many attempts');
              resetCountdown(60);
            }
            if (e.status === 400) setError('invalid code');
            setResetKey((prev) => prev + 1);
          },
        }
      );
      auth.loginAccessCodeSubmitted();
    },
    [isLockedOut, loginToken, email, auth, resetCountdown]
  );

  const handleResendCode = useCallback(() => {
    login({ email });
    auth.loginAccessCodeRerequested();
    setError('');
    setResetKey((prev) => prev + 1);
  }, [login, email, auth]);

  useEffect(() => {
    if (error === 'too many attempts' && timeLeft === 0) {
      setError('');
      setResetKey((prev) => prev + 1);
    }
  }, [error, timeLeft]);

  useEffect(() => {
    if (loginCodeParam && email) {
      submitCode(loginCodeParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthCard
      heading={t('codeCard.heading')}
      subheading={t('codeCard.subheading', { maskedEmail })}
    >
      <Styled.FormInput>
        <Styled.VerificationWrapper>
          <VerificationInput
            key={resetKey}
            inputProps={{ inputMode: 'numeric', disabled: isLockedOut }}
            length={6}
            onComplete={submitCode}
            placeholder=""
            validChars="0-9"
          />
        </Styled.VerificationWrapper>
      </Styled.FormInput>

      {error === 'invalid code' && (
        <Styled.InlineError>
          {t('errorMessages.invalidCodeInline')}
        </Styled.InlineError>
      )}

      {error === 'too many attempts' && (
        <Styled.InlineError>
          {t('errorMessages.tooManyAttemptsInline', { timeLeft })}
        </Styled.InlineError>
      )}

      {error === 'invalid code' && (
        <Styled.ResendCode as="button" onClick={handleResendCode}>
          {t('errorMessages.resendCode')}
        </Styled.ResendCode>
      )}

      {!error && <ErrorMessage email={email} />}
    </AuthCard>
  );
}
